import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import {
	Children,
	FC,
	isValidElement,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

const FADE = 56;

const arrowSX = (side: 'left' | 'right') => ({
	position: 'absolute' as const,
	top: '50%',
	[side]: '2px',
	transform: 'translateY(-50%)',
	zIndex: 3,
	width: '32px',
	height: '32px',
	color: '#2C2C2C',
	backgroundColor: '#fff',
	boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
	'&:hover': { backgroundColor: '#fff' },
});

const maskImage = (canPrev: boolean, canNext: boolean): string | undefined => {
	if (canPrev && canNext) {
		return `linear-gradient(to right, transparent 0, #000 ${FADE}px, #000 calc(100% - ${FADE}px), transparent 100%)`;
	}
	if (canNext) {
		return `linear-gradient(to right, #000 calc(100% - ${FADE}px), transparent 100%)`;
	}
	if (canPrev) {
		return `linear-gradient(to right, transparent 0, #000 ${FADE}px)`;
	}
	return undefined;
};

export const ColorScroller: FC<{ children: ReactNode; activeKey?: string }> = ({
	children,
	activeKey,
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);
	const childrenKey = Children.toArray(children)
		.map((child) => (isValidElement(child) ? String(child.key) : ''))
		.join('|');

	const update = useCallback(() => {
		const el = ref.current;
		if (!el) {
			return;
		}
		const { scrollLeft, scrollWidth, clientWidth } = el;
		setCanPrev(scrollLeft > 1);
		setCanNext(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
	}, []);

	const revealSelected = useCallback((behavior: ScrollBehavior) => {
		const el = ref.current;
		const selected = el?.querySelector<HTMLElement>('[data-selected="true"]');
		if (!el || !selected) {
			return;
		}
		const itemLeft = selected.offsetLeft;
		const itemRight = itemLeft + selected.offsetWidth;
		const viewLeft = el.scrollLeft;
		const viewRight = viewLeft + el.clientWidth;
		let target = viewLeft;
		if (itemRight > viewRight) {
			target = itemRight - el.clientWidth + FADE;
		} else if (itemLeft < viewLeft) {
			target = itemLeft - FADE;
		}
		el.scrollTo({ left: Math.max(0, target), behavior });
	}, []);

	useEffect(() => {
		const el = ref.current;
		if (!el) {
			return undefined;
		}
		revealSelected('auto');
		update();
		el.addEventListener('scroll', update, { passive: true });
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => {
			el.removeEventListener('scroll', update);
			observer.disconnect();
		};
	}, [update, revealSelected, childrenKey, activeKey]);

	const scroll = (direction: 1 | -1) => {
		const el = ref.current;
		if (!el) {
			return;
		}
		el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
	};

	const mask = maskImage(canPrev, canNext);

	return (
		<Box sx={{ position: 'relative' }}>
			{canPrev ? (
				<IconButton
					aria-label="Ver colores anteriores"
					onClick={() => scroll(-1)}
					sx={arrowSX('left')}
				>
					<ChevronLeft />
				</IconButton>
			) : null}

			<Box
				ref={ref}
				sx={{
					position: 'relative',
					display: 'flex',
					flexDirection: 'row',
					gap: '5px',
					overflowX: 'auto',
					scrollSnapType: 'x proximity',
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
					maskImage: mask,
					WebkitMaskImage: mask,
				}}
			>
				{children}
			</Box>

			{canNext ? (
				<IconButton aria-label="Ver más colores" onClick={() => scroll(1)} sx={arrowSX('right')}>
					<ChevronRight />
				</IconButton>
			) : null}
		</Box>
	);
};
